import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Search, User, Menu as MenuIcon, X } from "lucide-react";
import { siteInfo, navLinks } from "../mock";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 bg-white transition-shadow ${
        scrolled ? "shadow-md" : "shadow-sm"
      }`}
    >
      <div className="container-ka flex items-center justify-between py-4">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-12 h-12 rounded-full border-2 border-[var(--ka-teal)] flex items-center justify-center text-[var(--ka-teal)] font-serif text-xl leading-none">
            KA
          </div>
          <div className="leading-tight">
            <div className="font-serif text-[22px] md:text-[26px] text-[var(--ka-teal)]">
              {siteInfo.brand}
              <sup className="text-[10px] ml-0.5">{siteInfo.trademark}</sup>
            </div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-[var(--ka-muted)]">
              {siteInfo.tagline}
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              end={l.path === "/"}
              className={({ isActive }) =>
                `nav-link text-[14px] font-medium tracking-wide text-[var(--ka-text)] hover:text-[var(--ka-teal)] ${
                  isActive ? "active" : ""
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-3">
          <button
            aria-label="Search"
            onClick={() => setSearchOpen((s) => !s)}
            className="hidden md:flex w-9 h-9 items-center justify-center text-[var(--ka-text)] hover:text-[var(--ka-teal)] transition-colors"
          >
            <Search size={18} />
          </button>
          <button className="hidden md:inline-flex items-center gap-2 text-[14px] font-medium border border-gray-200 rounded-full px-4 py-2 hover:border-[var(--ka-teal)] hover:text-[var(--ka-teal)] transition-colors">
            <User size={16} /> Log In
          </button>
          <button
            className="lg:hidden w-10 h-10 inline-flex items-center justify-center text-[var(--ka-teal)]"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>
      </div>

      {/* Search bar */}
      {searchOpen && (
        <div className="border-t border-gray-100 bg-white">
          <div className="container-ka py-3 flex items-center gap-2">
            <Search size={18} className="text-[var(--ka-muted)]" />
            <input
              autoFocus
              type="text"
              placeholder="Search the menu, plans, recipes..."
              className="flex-1 outline-none text-sm py-2 bg-transparent"
            />
            <button
              className="text-sm text-[var(--ka-muted)] hover:text-[var(--ka-teal)]"
              onClick={() => setSearchOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white">
          <div className="container-ka py-4 flex flex-col gap-2">
            {navLinks.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.path === "/"}
                className={({ isActive }) =>
                  `py-2 text-[15px] font-medium ${
                    isActive
                      ? "text-[var(--ka-teal)]"
                      : "text-[var(--ka-text)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button className="mt-2 inline-flex items-center justify-center gap-2 text-[14px] font-medium border border-gray-200 rounded-full px-4 py-2 self-start">
              <User size={16} /> Log In
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
